import React from 'react';
import { useNavigate } from 'react-router-dom';

interface PageHeaderProps {
  /** Page title displayed next to the back button */
  title: string;
  /**
   * Navigation target when back button is clicked.
   * If not provided, uses navigate(-1) for browser history back.
   */
  backTo?: string;
  /** Custom back button label for accessibility */
  backLabel?: string;
  /** Optional right-side action slot */
  action?: React.ReactNode;
}

/**
 * Unified page header component for client portal pages.
 * Provides consistent back navigation and page title styling.
 *
 * Usage:
 * ```tsx
 * <PageHeader title="My Bookings" backTo="/u" />
 * <PageHeader title="Booking Details" /> // Uses history.back()
 * <PageHeader
 *   title="Expert Profile"
 *   backTo="/u"
 *   action={<FavoriteButton />}
 * />
 * ```
 */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  backTo,
  backLabel = 'Go back',
  action,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 mb-6">
      <div className="flex items-center gap-2 min-w-0">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="group flex items-center justify-center w-9 h-9 -ml-1.5 rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200 active:scale-95 flex-shrink-0"
          aria-label={backLabel}
        >
          <svg
            className="w-5 h-5 transition-transform duration-200 group-hover:-translate-x-0.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Title */}
        <h1 className="text-lg font-semibold text-gray-900 truncate">
          {title}
        </h1>
      </div>

      {/* Optional Action Slot */}
      {action && (
        <div className="flex-shrink-0">
          {action}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
